import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllClimbers, getClimber } from '../../services/firebase/climber';
import { Climber } from '../../models/user/Climber';
import HamburgerMenu from '../../components/HamburgerMenu';
import '../../styles/Dashboard.css';

const Dashboard: React.FC = () => {
  const [climbers, setClimbers] = useState<Climber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Check if current user has access to dashboard
  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      try {
        const climber = await getClimber(currentUser.uid);
        if (!climber || !climber.gymMemberships) {
          setHasAccess(false);
          setLoading(false);
          return;
        }

        // Check if user has 'fre' role in any gym
        const hasFreeRole = Object.values(climber.gymMemberships).some(
          role => role === 'fre'
        );
        
        setHasAccess(hasFreeRole);
        
        if (hasFreeRole) {
          // Fetch all climbers
          fetchClimbers();
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking access:', err);
        setHasAccess(false);
        setLoading(false);
      }
    };

    checkAccess();
  }, [currentUser]);

  const fetchClimbers = async () => {
    try {
      setLoading(true);
      const allClimbers = await getAllClimbers();
      setClimbers(allClimbers);
      setError(null);
    } catch (err) {
      console.error('Error fetching climbers:', err);
      setError('Failed to load climbers');
    } finally {
      setLoading(false);
    }
  };

  const handleClimberClick = (climberId: string) => {
    navigate(`/dashboard/climber/${climberId}`);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Admin Dashboard</h1>
          <HamburgerMenu />
        </div>
        <div className="loading-indicator">Loading...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Admin Dashboard</h1>
          <HamburgerMenu />
        </div>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the admin dashboard.</p>
          <button onClick={() => navigate('/gyms')}>Go to Gyms</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <HamburgerMenu />
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="dashboard-content">
        <div className="dashboard-section">
          <h2>Climbers</h2>
          <div className="climbers-list">
            {climbers.length === 0 ? (
              <p>No climbers found</p>
            ) : (
              climbers.map(climber => (
                <div 
                  key={climber.firebaseId} 
                  className="climber-card"
                  onClick={() => handleClimberClick(climber.firebaseId)}
                >
                  <div className="climber-avatar">
                    {climber.imageUrl ? (
                      <img 
                        src={climber.imageUrl} 
                        alt={climber.username || 'User'} 
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {(climber.username || climber.name || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="climber-info">
                    <h3>{climber.username || 'Unknown User'}</h3>
                    {climber.name && <p className="climber-name">{climber.name}</p>}
                    <p className="climber-stats">
                      {climber.getTotalClimbs()} climbs • {climber.getGymIds().length} gyms
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
